// Pure MIME / RFC 2047 encoding helpers for building Gmail API `raw` messages.
// No external dependencies — safe to unit-test in isolation.
//
// Why this exists: Gmail decodes a message's BODY using the part's Content-Type
// charset, but it NEVER charset-decodes HEADERS. Non-ASCII bytes placed raw in a
// header (e.g. an em dash in the Subject) get read as Latin-1/CP1252 and render
// as mojibake ("—" -> "â€""). Headers must therefore be RFC 2047 "encoded-word"
// wrapped. The whole message is then base64url-encoded from its UTF-8 bytes for
// the Gmail API `raw` field — via TextEncoder, not escape()/btoa() Latin-1 hacks.

// Keeps each "=?UTF-8?B?...?=" encoded-word within RFC 2047's 75-char limit:
// 10 chars of "=?UTF-8?B?" + 2 chars of "?=" + ceil(45/3)*4 = 60 base64 chars = 72.
const HEADER_WORD_MAX_BYTES = 45;

function base64FromBytes(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

const isAscii = (str) => /^[\x00-\x7F]*$/.test(str);

/**
 * RFC 2047-encode a header value when it contains non-ASCII characters.
 * Pure-ASCII values pass through untouched. Non-ASCII values become one or more
 * base64 encoded-words, split on UTF-8 character boundaries and folded with
 * CRLF + space so no single encoded-word exceeds the 75-char limit.
 * @param {string} value
 * @returns {string}
 */
export function encodeHeaderWord(value) {
  if (isAscii(value)) return value;
  const bytes = new TextEncoder().encode(value);
  const words = [];
  let i = 0;
  while (i < bytes.length) {
    let end = Math.min(i + HEADER_WORD_MAX_BYTES, bytes.length);
    // Never split a multi-byte UTF-8 sequence across two encoded-words:
    // back up while the boundary byte is a continuation byte (10xxxxxx).
    while (end < bytes.length && (bytes[end] & 0xc0) === 0x80) end--;
    words.push(`=?UTF-8?B?${base64FromBytes(bytes.slice(i, end))}?=`);
    i = end;
  }
  return words.join('\r\n ');
}

/**
 * Build an RFC 5322 message with a UTF-8 text body and RFC 2047-encoded headers.
 * @param {{to: string, subject: string, body: string, extraHeaders?: string[]}} fields
 * @returns {string}
 */
// SECURITY-AGENTS-2026-10-14 (header injection): a lead's email or name can carry a line break or a comma, which
// would add recipients or headers to a message a recruiter sends. The recipient must be ONE address (refused
// otherwise); a line break in the subject becomes a space; an extra header with a line break is refused.
export const SINGLE_ADDRESS = /^[^\s@,;<>"()\\]+@[^\s@,;<>"()\\]+\.[^\s@,;<>"()\\]+$/;
const LINE_BREAK = /[\r\n\u0000]/;

export function buildMimeMessage({ to, subject, body, extraHeaders = [] }) {
  const addr = String(to == null ? '' : to).trim();
  if (!SINGLE_ADDRESS.test(addr)) throw new Error('Not sent: the recipient is not a single email address.');
  for (const h of extraHeaders) {
    if (LINE_BREAK.test(String(h))) throw new Error('Not sent: a message header contains a line break.');
  }
  const safeSubject = String(subject == null ? '' : subject).replace(/[\r\n\u0000]+/g, ' ');
  const headers = [
    `To: ${addr}`,
    `Subject: ${encodeHeaderWord(safeSubject)}`,
    'MIME-Version: 1.0',
    'Content-Type: text/plain; charset="UTF-8"',
    'Content-Transfer-Encoding: 8bit',
    ...extraHeaders,
  ];
  return headers.join('\r\n') + '\r\n\r\n' + body;
}

/**
 * Encode a full message string to base64url of its UTF-8 bytes, as required by
 * the Gmail API `raw` field. Operates on UTF-8 bytes via TextEncoder.
 * @param {string} message
 * @returns {string}
 */
export function toBase64Url(message) {
  const bytes = new TextEncoder().encode(message);
  return base64FromBytes(bytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Build + encode in one step: returns the base64url value ready for `{ raw }`.
 * @param {{to: string, subject: string, body: string, extraHeaders?: string[]}} fields
 * @returns {string}
 */
export function encodeRawMessage(fields) {
  return toBase64Url(buildMimeMessage(fields));
}
